import type { ClassValue } from 'clsx';

import { clsxm } from '@vs-shop/utils';
import { getCouponDiscountPercentage } from '@vs-shop/domain/price/getDiscountPercentageFromPrices';
import {
  ProductCouponAmounts,
  ProductCouponCode,
  ProductCouponCodeVariant,
  ProductCouponMark,
  ProductCouponPrice,
  ProductCouponSaving,
} from '@vs-shop/commerce-ui/productCoupon';

import { getDefaultCurrency } from '../../../config/currency';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../utils/intl';

import { IntlMoney, IntlPercentage } from '../intl/Intl';

type Props = {
  price: number;
  priceWithCode: number;
  codeName: string;
  className?: ClassValue;
};

/**
 * rcprofi's binding of the shared coupon mark's stating face: the saving, the
 * price it leaves and the code, each formatted in this tenant's currency and
 * locale.
 */
export default function ProductCouponStatic({ price, priceWithCode, codeName, className }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const currency = getDefaultCurrency();

  const discountPercentage = getCouponDiscountPercentage(priceWithCode, price);

  return (
    <ProductCouponMark className={clsxm(className)}>
      <ProductCouponAmounts>
        {/* PERCENTAGE */}
        <ProductCouponSaving>
          &#8208;
          <IntlPercentage value={String(discountPercentage)} />
        </ProductCouponSaving>

        {/* PRICE */}
        <ProductCouponPrice>
          <IntlMoney value={{ amount: priceWithCode, currency: currency }} />
        </ProductCouponPrice>
      </ProductCouponAmounts>

      {/* CODE */}
      <ProductCouponCode variant={ProductCouponCodeVariant.PLAIN}>
        {intl.formatMessage(
          {
            id: 'product.productDiscountBannerStatic.code',
            defaultMessage: 'Kód: {code}',
          },
          {
            code: (
              <span key="code" className="font-semibold">
                {codeName}
              </span>
            ),
          }
        )}
      </ProductCouponCode>
    </ProductCouponMark>
  );
}
